import {
  AdminSpinner,
  ListSpinner,
  SimpleLocationEntity,
} from '@vgrubs/components';
// Direct import: this one is deliberately outside the barrel.
import { NoDataComponent } from 'components/src/adminComponents/NoDataComponent/NoDataComponent';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { ProblemSummary } from './components/ProblemSummary';
import {
  ALL_LOCATIONS,
  SystemProblemFilters,
} from './components/SystemProblemFilters';
import { SystemProblemModal } from './components/SystemProblemModal';
import { SystemProblemRow } from './components/SystemProblemRow';
import {
  problemAction,
  SystemProblemView,
  viewStatus,
} from './helpers/systemProblemLabels';
import { useLocationPhones } from './hooks/useLocationPhones';
import { useSystemProblem } from './hooks/useSystemProblem';
import { useSystemProblemFilters } from './hooks/useSystemProblemFilters';
import { useSystemProblemsList } from './hooks/useSystemProblemsList';
import { useSystemProblemsSocket } from './hooks/useSystemProblemsSocket';

/**
 * What an empty list says. Nothing at all on the first screen (every status,
 * no filter) is good news and reads as such; an empty filter is only an
 * empty filter.
 */
const emptyState = (view: SystemProblemView, filtered: boolean) => {
  if (!filtered && view === 'all') {
    return { title: 'All clear', description: 'No system problems so far.' };
  }
  if (!filtered && view === 'open') {
    return { title: 'All clear', description: 'No open system problems.' };
  }
  return {
    title: 'Nothing here',
    description: 'No system problems match these filters.',
  };
};

/**
 * The System Problems page's content: what the backend noticed on its own
 * and wants somebody on the team to see. The counters and category chips on
 * top are both the open totals and the list's filters; under them location
 * and status, then every problem newest first, each with the one thing to do
 * about it, and its detail on a click.
 *
 * One shared list for every admin and support user, kept live by its own
 * socket while the page is open. The Dashboard's Orders tab only says when
 * something is critical, and links here.
 */
export const SystemProblemsTab = () => {
  useSystemProblemsSocket();
  const filters = useSystemProblemFilters();

  /* The picker wants the whole location, the URL only carries what it takes
     to restore one. Never null: see ALL_LOCATIONS. */
  const [location, setLocation] = useState<SimpleLocationEntity>(
    filters.locationId
      ? ({
          id: filters.locationId,
          name: filters.locationName ?? '',
        } as SimpleLocationEntity)
      : ALL_LOCATIONS
  );

  const list = useSystemProblemsList({
    status: viewStatus(filters.view),
    locationId: filters.locationId,
    severity: filters.severity,
    category: filters.category,
  });

  const [sentinelRef, sentinelInView] = useInView({ rootMargin: '400px' });

  useEffect(() => {
    if (sentinelInView && list.hasNextPage && !list.isFetchingNextPage) {
      list.fetchNextPage();
    }
  }, [sentinelInView, list]);

  /* One request for every open row whose action is a phone call. */
  const phoneOf = useLocationPhones(
    list.problems
      .filter(
        (row) =>
          row.status === 'open' &&
          problemAction(row)?.kind === 'call' &&
          row.locationId
      )
      .map((row) => row.locationId as number)
  );

  const { problem } = useSystemProblem(filters.problemId);

  return (
    <>
      <ProblemSummary
        severity={filters.severity}
        onSeverity={filters.setSeverity}
        category={filters.category}
        onCategory={filters.setCategory}
      />

      <SystemProblemFilters
        view={filters.view}
        onViewChange={filters.setView}
        openCount={list.open}
        location={location}
        setLocation={setLocation}
        onLocationPicked={(picked) =>
          filters.setLocation(picked?.id ?? null, picked?.name)
        }
      />

      {list.isLoading && <AdminSpinner />}

      {list.problems.map((row) => (
        <SystemProblemRow
          key={row.id}
          problem={row}
          onOpen={filters.openProblem}
          phone={phoneOf(row.locationId)}
        />
      ))}

      {/* Said apart from the empty state: "All clear" over a failed fetch
          would tell support there is nothing to look at. */}
      {list.isError && list.problems.length === 0 && (
        <NoDataComponent
          title="Could not load system problems"
          description="Try again in a moment."
        />
      )}

      {!list.isLoading && !list.isError && list.problems.length === 0 && (
        <NoDataComponent
          {...emptyState(
            filters.view,
            !!(filters.locationId || filters.severity || filters.category)
          )}
        />
      )}

      <ListSpinner
        loading={list.isFetchingNextPage}
        observerRef={sentinelRef}
      />

      <SystemProblemModal
        isOpen={filters.problemId !== null}
        problem={problem}
        onClose={() => filters.openProblem(null)}
      />
    </>
  );
};
